import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useTheme } from '@mui/material/styles';
import MichiTooltip from './MichiTooltip';
import { useCatAssistant } from '../../contexts/CatAssistantContext';

const BusinessTour: React.FC = () => {
  const theme = useTheme();
  const { say } = useCatAssistant();
  const [run, setRun] = useState(false);

  const steps: Step[] = [
    {
      target: 'body',
      title: '¡Bienvenido a tu Negocio! 🏪',
      content: '¡Hola! Soy Michi y estoy aquí para mostrarte cómo gestionar tu negocio de manera fácil y divertida. ¡Vamos a explorar juntos! 😺',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="business-header"]',
      title: 'Información de tu Negocio',
      content: 'Aquí puedes ver el nombre y descripción de tu negocio. ¡Es la cara de tu emprendimiento!',
      placement: 'bottom',
    },
    {
      target: '[data-tour="business-tabs"]',
      title: 'Navegación por Pestañas',
      content: 'Aquí tienes todas las secciones de tu negocio: Resumen, Agendas, Configuración y Compartir. Cada una tiene funciones específicas.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="tab-resumen"]',
      title: 'Pestaña Resumen',
      content: 'Aquí verás estadísticas rápidas de tu negocio: agendas activas, capacidad total y un calendario con todas tus reservas.',
      placement: 'right',
    },
    {
      target: '[data-tour="tab-agendas"]',
      title: 'Pestaña Agendas',
      content: 'Gestiona tus agendas aquí. Crea nuevas agendas para diferentes servicios como canchas, mesas, habitaciones, etc.',
      placement: 'right',
    },
    {
      target: '[data-tour="tab-configuracion"]',
      title: 'Pestaña Configuración',
      content: 'Personaliza la información de tu negocio: nombre, descripción y URL personalizada.',
      placement: 'right',
    },
    {
      target: '[data-tour="tab-compartir"]',
      title: 'Pestaña Compartir',
      content: 'Comparte los enlaces de tu negocio y servicios específicos con tus clientes.',
      placement: 'right',
    },
    {
      target: '[data-tour="cancelar-turnos"]',
      title: 'Cancelar Turnos Masivamente',
      content: 'Si necesitas cancelar varias reservas a la vez (por ejemplo, por mantenimiento), usa este botón.',
      placement: 'left',
    },
    {
      target: '[data-tour="nueva-agenda"]',
      title: 'Crear Nueva Agenda',
      content: '¡Crea nuevas agendas para expandir tus servicios! Elige el tipo, capacidad y descripción.',
      placement: 'left',
    },
    {
      target: 'body',
      title: '¡Ya estás listo! 🚀',
      content: 'Ahora conoces todas las herramientas para gestionar tu negocio. ¡Recuerda que siempre estoy aquí para ayudarte! Si tienes dudas, solo pregúntame. 😸',
      placement: 'center',
    }
  ];

  useEffect(() => {
    const tourCompleted = localStorage.getItem('business_tour_completed');
    if (!tourCompleted) {
      // Small delay to ensure everything is rendered
      const timer = setTimeout(() => {
        setRun(true);
        say('¡Miau! ¿Quieres que te enseñe a usar tu negocio? 😺', 'greeting');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [say]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
      localStorage.setItem('business_tour_completed', 'true');
      say('¡Excelente! Ahora eres un maestro en gestión de negocios. ¡A trabajar! 💪', 'success');
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      tooltipComponent={MichiTooltip}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: theme.palette.primary.main,
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          mixBlendMode: 'hard-light'
        }
      }}
      floaterProps={{
        // disableAnimation: true, // Not supported in v3 types yet
      }}
    />
  );
};

export default BusinessTour;